// Work kit — issue TRANSITIONS controller (wave 4, Track D). Mounted on the
// status-lozenge cluster in the header; owns the pinned semantics:
//
//   - menu pick with NO required fields → optimistic lozenge swap + emit
//     ui--work:transition immediately (pin D1),
//   - menu pick WITH required fields → reveal the matching form section in
//     the shared modal (never a kit-built form; pin D2),
//   - Esc inside the modal closes THE MODAL only — the field-level capture
//     veto (issue_field_controller) already swallowed Esc while an editor
//     was open; the drawer must not close (pin D3),
//   - submit (Enter via the form's hidden submit affordance, or Apply) →
//     optimistic lozenge swap → emit (one event per submit, pin D4). Every
//     emit carries a per-gesture `gesture_token` (the drag kernel's idiom,
//     echoed by the host on the ack),
//   - transition-ack ok → adopt server truth (status + tone) or keep the
//     optimistic label; !ok → rollback + EXACTLY ONE toast (pin D5). An ack
//     whose echoed token does not match the in-flight gesture is STALE — it
//     is ignored and `ui--work:transition-stale` tells the host to refetch
//     (the cell-stale grammar), so an older failing transition never
//     clobbers a newer optimistic status,
//   - the transition modal is looked up INSIDE this cluster (two shells may
//     render the same issue id; a document-wide id lookup would resolve to
//     the first match),
//   - focus returns to the lozenge button after the modal closes (pin D6).
//
// Events + toasts go through the shared seam; the kit performs no HTTP.

import { Controller } from "@hotwired/stimulus"
import { adoptOrRollback, emitFromIssueRoot, showToast, toastRegionFor } from "../../../lib/work/mutation_seam"

const TRANSITION_EVENT = "ui--work:transition"
const TRANSITION_ACK_EVENT = "ui--work:transition-ack"
// The kit's stale-event grammar (ui--work:cell-stale in the editable-table
// engine): a stale ack tells the host to refetch instead of reverting.
const TRANSITION_STALE_EVENT = "ui--work:transition-stale"

// Gesture token generator — the drag kernel's `newGestureToken` idiom,
// scoped to this controller (module-local counter + crypto UUID, Math.random
// fallback). One token per transition emit: carried on the intent detail and
// echoed by the host on the ack, so a STALE failing ack (an earlier gesture
// answering after a newer optimistic swap) is recognized and ignored instead
// of clobbering the newer optimistic status.
let tokenCounter = 0
function newTransitionToken() {
  tokenCounter += 1
  if (globalThis.crypto?.randomUUID) {
    return `work-transition-${tokenCounter}-${globalThis.crypto.randomUUID()}`
  }
  return `work-transition-${tokenCounter}-${Math.random().toString(36).slice(2)}`
}

export default class extends Controller {
  static values = {
    issueId: String,
    status: String,
    statusTone: String,
    errorMessage: { type: String, default: "" },
  }

  static targets = ["lozengeButton", "statusBadge", "transitionForm", "live"]

  connect() {
    this._teardown()
    this._optimistic = null // { name } while a transition is in flight
    this._gestureToken = null // the in-flight gesture's echoed token
    this._onAck = this._handleAck.bind(this)
    this._onModalClose = this._handleModalClose.bind(this)
    document.addEventListener(TRANSITION_ACK_EVENT, this._onAck)
    window.addEventListener("modal:close", this._onModalClose)
  }

  disconnect() {
    this._teardown()
  }

  // Teardown-first re-bind (dashboard_grid idiom, ghost-closure family): a
  // same-instance double-connect (Turbo cache restore racing a morph) must
  // not stack a second document ack closure NOR a second window modal:close
  // closure — the orphaned ghosts re-handle acks and close events (double
  // toasts, double revert). Removing first is a no-op when nothing is bound.
  _teardown() {
    document.removeEventListener(TRANSITION_ACK_EVENT, this._onAck)
    window.removeEventListener("modal:close", this._onModalClose)
    this._onAck = null
    this._onModalClose = null
  }

  // --- menu pick ---

  pick(event) {
    const item = event.currentTarget
    const transitionId = item.getAttribute("data-work-transition-id") || ""
    const transitionName = item.getAttribute("data-work-transition-name") || ""
    const required = item.getAttribute("data-work-transition-required") === "true"

    if (required) {
      this._openModalFor(transitionId)
    } else {
      this._beginGesture(transitionName, transitionId, {})
    }
  }

  // --- modal submit paths (one event per submit, pin D4) ---

  // Native form submit (Enter hits the hidden submit affordance).
  submitForm(event) {
    event.preventDefault()
    const form = event.currentTarget
    const transitionId = form.getAttribute("data-work-transition-id") || ""

    const fields = {}
    new FormData(form).forEach((value, key) => {
      if (typeof value === "string" && value !== "") fields[key] = value
    })

    const name = form.getAttribute("data-work-transition-name") || ""
    this._beginGesture(name, transitionId, fields)
    this._closeModal()
  }

  // Apply button → route through the visible form so Enter and Apply share
  // one code path (and one emit).
  submitModal() {
    const form = this.transitionFormTargets.find((el) => !el.hidden)
    if (!form) return
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
    }
  }

  // --- internals ---

  _openModalFor(transitionId) {
    const form = this.transitionFormTargets.find((el) => el.getAttribute("data-work-transition-id") === transitionId)
    if (!form) return
    this.transitionFormTargets.forEach((el) => {
      el.hidden = el !== form
    })

    const modal = this._modalElement()
    if (!modal) return
    const controller = this.application.getControllerForElementAndIdentifier(modal, "ui--modal")
    if (controller?.open) {
      controller.open()
    } else if (typeof modal.show === "function") {
      modal.show()
    }
  }

  _closeModal() {
    const modal = this._modalElement()
    if (!modal) return
    const controller = this.application.getControllerForElementAndIdentifier(modal, "ui--modal")
    if (controller?.close) {
      controller.close()
    } else if (modal.open && typeof modal.close === "function") {
      modal.close()
    }
  }

  // The transition modal is a DESCENDANT of this cluster (the component
  // renders it in place — the dialog opens via the `open` attribute, never
  // teleported to body), so the lookup is scoped HERE. A document-wide id
  // lookup would resolve to the FIRST match when the same issue is open in
  // two shells (page + drawer), each owning its own modal. Iteration lookup
  // — the id interpolates host data, never a selector.
  _modalElement() {
    const id = `work-issue-transition-modal-${this.issueIdValue}`
    return Array.from(this.element.querySelectorAll("dialog")).find((node) => node.id === id) || null
  }

  // Focus returns to the lozenge button when the modal closes (pin D6) —
  // regardless of whether the close came from Cancel, Esc, or a successful
  // submit.
  _handleModalClose(event) {
    const modal = event.target
    if (!(modal instanceof Element)) return
    // Same-instance guard: the id interpolates the issue id, which two shells
    // can share — only a close for a modal this cluster OWNS may move focus.
    if (!this.element.contains(modal)) return
    if (modal.id !== `work-issue-transition-modal-${this.issueIdValue}`) return
    if (!this.hasLozengeButtonTarget) return
    if (this.element.contains(document.activeElement)) return
    requestAnimationFrame(() => this.lozengeButtonTarget.focus())
  }

  // One gesture = one token: minted here, stamped on the optimistic swap,
  // and carried on the emitted intent (`gesture_token`, the drag kernel's
  // field name). Both menu picks and modal submits route through this seam.
  _beginGesture(name, transitionId, fields) {
    this._gestureToken = newTransitionToken()
    this._applyOptimistic(name)
    this._emit(transitionId, fields, this._gestureToken)
  }

  // Optimistic lozenge: the destination status label replaces the current
  // one immediately (the transition name IS the destination status in the
  // pinned contract); server truth lands via the ack.
  _applyOptimistic(name) {
    if (!name || !this.hasStatusBadgeTarget) return
    this._optimistic = { name: name }
    this._setBadgeLabel(name)
    this._announce(`${this.statusValue} → ${name}`)
  }

  _setBadgeLabel(text) {
    const badge = this.statusBadgeTarget
    const inner = badge.querySelector("span") || badge
    inner.textContent = text
  }

  _emit(transitionId, fields, gestureToken) {
    // Emission goes through the shared seam helper (emitFromIssueRoot) —
    // same body-root dispatch the other work controllers use.
    emitFromIssueRoot(this.element, TRANSITION_EVENT, {
      issue_id: this.issueIdValue,
      transition_id: transitionId,
      fields: fields,
      gesture_token: gestureToken,
    })
    showToast(toastRegionFor(this.element), "", "loading")
  }

  _handleAck(event) {
    const detail = event.detail || {}
    if (detail.issue_id !== this.issueIdValue) return

    // Stale-ack guard: the host echoes the emitted per-gesture token. A
    // tokened ack that does not match the in-flight gesture is an OLDER
    // gesture answering after a newer optimistic swap — its revert would
    // clobber that swap, so it is ignored and the host is told to refetch
    // (the kit's cell-stale grammar). A tokenless (legacy) ack cannot be
    // attributed to a gesture and keeps the issue_id-only matching; an echoed
    // EMPTY string counts as tokenless too (the sibling controllers'
    // op_token normalization — "" !== token would drop every such ack).
    if (
      this._gestureToken &&
      typeof detail.gesture_token === "string" &&
      detail.gesture_token &&
      detail.gesture_token !== this._gestureToken
    ) {
      emitFromIssueRoot(this.element, TRANSITION_STALE_EVENT, {
        issue_id: this.issueIdValue,
        gesture_token: detail.gesture_token,
      })
      return
    }

    const region = toastRegionFor(this.element)
    const outcome = adoptOrRollback({
      ack: detail,
      region,
      applyAdopt: (ack) => this._adopt(ack),
      applyRevert: () => this._revert(),
      errorMessage: this.errorMessageValue,
    })

    if (outcome === "adopted") {
      this._optimistic = null
      this._gestureToken = null
      this._announce(this.statusValue)
    } else if (outcome === "reverted") {
      this._optimistic = null
      this._gestureToken = null
      this._announce(this.errorMessageValue)
    }
  }

  _adopt(ack) {
    if (typeof ack.status === "string" && ack.status !== "") {
      this.statusValue = ack.status
      if (this.hasStatusBadgeTarget) this._setBadgeLabel(ack.status)
    }
    // status_tone is relayed as host data (D5 pin) — the kit ships no JS
    // tone-class map (never a second display path): the badge's tone classes
    // converge at the next server render. The transition name IS the
    // destination status the optimistic swap already showed.
    if (
      typeof ack.status_tone === "string" &&
      ack.status_tone !== "" &&
      this.hasStatusBadgeTarget
    ) {
      this.statusToneValue = ack.status_tone
      this.statusBadgeTarget.setAttribute("data-work-status-tone", ack.status_tone)
    }
  }

  _revert() {
    if (this.statusValue && this.hasStatusBadgeTarget) this._setBadgeLabel(this.statusValue)
  }

  _announce(text) {
    if (!this.hasLiveTarget) return
    this.liveTarget.textContent = text
  }
}
